import React, { memo, useCallback, useMemo, useState } from 'react';

import { Box, Button, Collapse, Skeleton, Typography } from '@mui/material';
import { ArrowCheckIcon, NoteIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { formatGuidelineCitationText } from 'constants/aiChat';
import { IconSize } from 'constants/coreDesignSystem';

import { parseFormattedContent } from '../CitedContent/CitedContent.utils';

import { GUIDELINE_EXPANDER_CARD_LABELS } from './GuidelineExpanderCard.constants';
import {
  actionButtonStyles,
  actionsContainerStyles,
  cardContainerStyles,
  contentStyles,
  excerptStyles,
  getChevronStyles,
  headerLeftStyles,
  headerStyles,
  headerTextContainerStyles,
  iconStyles,
  sectionTextStyles,
  titleTextStyles,
  unavailableCardContainerStyles,
  unavailableIconStyles,
} from './GuidelineExpanderCard.styles';
import { GuidelineExpanderCardTestIds } from './GuidelineExpanderCard.testIds';

const GuidelineExpanderCard = ({
  citation,
  onReadFullSection,
  onAskAboutGuideline,
  isLoadingFullContent,
  isActionsHidden = false,
  isUnavailable = false,
  unavailableLabel = null,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const handleKeyDown = useCallback(event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsExpanded(prev => !prev);
    }
  }, []);

  const handleReadFullSection = useCallback(() => {
    onReadFullSection?.(citation);
  }, [citation, onReadFullSection]);

  const handleAskAboutGuideline = useCallback(() => {
    onAskAboutGuideline?.(citation);
  }, [citation, onAskAboutGuideline]);

  const isLoading = !isUnavailable && !citation.title && !citation.source;
  const citationText = formatGuidelineCitationText(citation);

  const renderedExcerpt = useMemo(
    () => parseFormattedContent(citation.excerpt),
    [citation.excerpt],
  );

  const headerSxStyles = useMemo(
    () => ({
      ...headerStyles,
      cursor: isLoading ? 'default' : 'pointer',
    }),
    [isLoading],
  );

  // LEN-213: a citation whose details could not be loaded (unknown ref, 403, network)
  // shows a compact, non-interactive "unavailable" header instead of a skeleton that
  // never resolves.
  if (isUnavailable) {
    return (
      <Box
        data-testid={GuidelineExpanderCardTestIds.UNAVAILABLE}
        role="note"
        sx={unavailableCardContainerStyles}
      >
        <Box sx={unavailableIconStyles}>
          <NoteIcon size={14} />
        </Box>
        <Box sx={headerTextContainerStyles}>
          {unavailableLabel ? (
            <Typography color="text.secondary" variant="medium14">
              {unavailableLabel}
            </Typography>
          ) : null}
          <Typography color="text.secondary" variant="regular12">
            {GUIDELINE_EXPANDER_CARD_LABELS.UNAVAILABLE}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={cardContainerStyles}>
      <Box
        aria-expanded={isExpanded}
        aria-label={
          isLoading
            ? 'Loading guideline details'
            : `${isExpanded ? 'Collapse' : 'Expand'} guideline: ${citation.title}`
        }
        role={isLoading ? 'presentation' : 'button'}
        sx={headerSxStyles}
        tabIndex={isLoading ? -1 : 0}
        onClick={isLoading ? undefined : handleToggle}
        onKeyDown={isLoading ? undefined : handleKeyDown}
      >
        <Box sx={headerLeftStyles}>
          {!isLoading && (
            <Box sx={getChevronStyles(isExpanded)}>
              <ArrowCheckIcon size={IconSize[16]} />
            </Box>
          )}
          <Box sx={iconStyles}>
            <NoteIcon size={14} />
          </Box>
          <Box sx={headerTextContainerStyles}>
            {isLoading ? (
              <>
                <Skeleton animation="wave" height={16} variant="text" width={120} />
                <Skeleton animation="wave" height={20} variant="text" width="70%" />
              </>
            ) : (
              <>
                <Typography sx={sectionTextStyles} variant="medium14">
                  {citationText}
                </Typography>
                <Typography sx={titleTextStyles} variant="regular14">
                  {citation.title}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {!isLoading && (
        <Collapse in={isExpanded}>
          <Box sx={contentStyles}>
            <Typography component="div" sx={excerptStyles} variant="regular14">
              {renderedExcerpt}
            </Typography>

            {!isActionsHidden && (!!onReadFullSection || !!onAskAboutGuideline) && (
              <Box sx={actionsContainerStyles}>
                {!!onReadFullSection && (
                  <Button
                    data-testid={GuidelineExpanderCardTestIds.READ_FULL_SECTION_BUTTON}
                    disabled={isLoadingFullContent}
                    size="small"
                    sx={actionButtonStyles}
                    variant="text"
                    onClick={handleReadFullSection}
                  >
                    {isLoadingFullContent ? 'Loading...' : 'Read Full Section'}
                  </Button>
                )}
                {!!onAskAboutGuideline && (
                  <Button
                    data-testid={GuidelineExpanderCardTestIds.ASK_ABOUT_GUIDELINE_BUTTON}
                    size="small"
                    sx={actionButtonStyles}
                    variant="text"
                    onClick={handleAskAboutGuideline}
                  >
                    Ask About This Guideline
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

GuidelineExpanderCard.propTypes = {
  citation: PropTypes.shape({
    id: PropTypes.string.isRequired,
    source: PropTypes.string.isRequired,
    section: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    excerpt: PropTypes.string.isRequired,
  }).isRequired,
  isLoadingFullContent: PropTypes.bool,
  isActionsHidden: PropTypes.bool,
  isUnavailable: PropTypes.bool,
  unavailableLabel: PropTypes.string,
  onReadFullSection: PropTypes.func,
  onAskAboutGuideline: PropTypes.func,
};

export default memo(GuidelineExpanderCard);
